import { useMemo } from 'react';
import type { Commonv1Location } from '@/generated/hooks/astral/models';
import { logger } from '@/shared/utils/logger';
import { captureException } from '@/modules/sentry';
import {
  mapSpyLocationToAopCharacterRanges,
  type ICharacterRange,
} from '@/shared/utils/source-map';
import type { V1CodeMapping } from '@/generated/hooks/grimoire/models';

/**
 * Supported highlight types for editor highlighting.
 *
 * `running` marks the step executing right now — distinct from `executed`, which
 * marks everything already finished.
 */
export type THighlightType =
  | 'exception'
  | 'executed'
  | 'resolvedException'
  | 'running';

/**
 * Source of the highlight, used to determine badge behavior.
 * - 'activeException': Highlight from active exception (awaiting_guidance state)
 * - 'runLog': Highlight from RunLog hover/select interaction
 */
export type THighlightSource = 'activeException' | 'runLog';

/**
 * A highlight with its type and character ranges.
 */
export interface ITypedHighlight {
  /** The type of highlight */
  type: THighlightType;
  /** Character ranges to highlight */
  ranges: ICharacterRange[];
  /** Source of the highlight (activeException or runLog) */
  source?: THighlightSource;
}

/**
 * Input for computing highlights - location with its type.
 */
export interface IHighlightInput {
  /** The type of highlight */
  type: THighlightType;
  /** The location with byte offsets */
  location?: Commonv1Location | null;
}

/**
 * Result type for useHighlightRanges hook.
 */
export interface IHighlightRangesResult {
  /** The highlight ranges or empty array if not available */
  ranges: ICharacterRange[];
  /** Whether an error occurred during highlight computation */
  hasError: boolean;
}

/**
 * Result type for useMultipleHighlightRanges hook.
 */
export interface IMultipleHighlightRangesResult {
  /** Array of typed highlights with their ranges */
  highlights: ITypedHighlight[];
  /** Whether any error occurred during highlight computation */
  hasError: boolean;
}

/**
 * Computes highlight ranges for a location in AOP code.
 * Maps a spy location to AOP character ranges using code mapping.
 *
 * This is a standalone function that can be called imperatively.
 * Use this when you need to compute ranges outside of a React component
 * or when you need more control over when the computation happens.
 *
 * Handles edge cases:
 * - Missing location (returns empty array)
 * - Missing code mapping or AOP text (returns empty array)
 * - Errors during mapping (returns empty array with error logging and Sentry capture)
 *
 * @param location - The spy location with byte offsets
 * @param codeMapping - The code mapping from spy to AOP
 * @param aopText - The AOP text content
 * @returns Object containing the highlight ranges and error state
 *
 * @example
 * const { ranges, hasError } = computeHighlightRanges({
 *   location: { start_byte: '0', end_byte: '100' },
 *   codeMapping: automationData?.code_mapping,
 *   aopText: automationData?.english_code,
 * });
 */
export function computeHighlightRanges({
  location,
  codeMapping,
  aopText,
}: {
  location?: Commonv1Location | null;
  codeMapping?: V1CodeMapping | null;
  aopText?: string | null;
}): IHighlightRangesResult {
  // Early return if location is missing
  if (!location || !location.start_byte || !location.end_byte) {
    return { ranges: [], hasError: false };
  }

  // Check if required data is missing
  if (!codeMapping || !aopText) {
    return { ranges: [], hasError: false };
  }

  try {
    const ranges = mapSpyLocationToAopCharacterRanges({
      spyLocation: location,
      codeMapping,
      aopText,
    });
    return { ranges, hasError: false };
  } catch (error) {
    logger.error(
      'computeHighlightRanges: Error computing highlight ranges',
      error,
      {
        hasLocation: !!location,
        hasCodeMapping: !!codeMapping,
        hasAopText: !!aopText,
      },
    );

    // Capture error to Sentry for monitoring
    captureException(error as Error, {
      tags: {
        component: 'computeHighlightRanges',
        action: 'mapSpyLocationToAopCharacterRanges',
      },
      extra: {
        hasLocation: !!location,
        hasCodeMapping: !!codeMapping,
        hasAopText: !!aopText,
        locationStartByte: location.start_byte,
        locationEndByte: location.end_byte,
      },
    });

    return { ranges: [], hasError: true };
  }
}

/**
 * Hook to compute highlight ranges for a location in AOP code.
 * Maps a spy location to AOP text using code mapping.
 *
 * This is a memoized React hook wrapper around `computeHighlightRanges`.
 * Use this in React components for automatic memoization.
 *
 * Handles edge cases:
 * - Missing location (returns empty array)
 * - Missing code mapping or AOP text (returns empty array)
 * - Errors during mapping (returns empty array with error logging and Sentry capture)
 *
 * @param location - The location with byte offsets
 * @param codeMapping - The code mapping from spy to AOP
 * @param aopText - The AOP text content
 * @returns Object containing the highlight ranges and error state
 *
 * @example
 * const { ranges, hasError } = useHighlightRanges({
 *   location: exception?.location,
 *   codeMapping: automationData?.code_mapping,
 *   aopText: automationData?.english_code,
 * });
 */
export function useHighlightRanges({
  location,
  codeMapping,
  aopText,
}: {
  location?: Commonv1Location | null;
  codeMapping?: V1CodeMapping | null;
  aopText?: string | null;
}): IHighlightRangesResult {
  // Use granular location dependencies to ensure recomputation when location changes
  const locationStartByte = location?.start_byte;
  const locationEndByte = location?.end_byte;

  return useMemo(() => {
    return computeHighlightRanges({ location, codeMapping, aopText });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locationStartByte and locationEndByte trigger recomputation when location properties change
  }, [location, locationStartByte, locationEndByte, codeMapping, aopText]);
}

/**
 * Hook to compute highlight ranges for multiple locations in AOP code.
 * Maps multiple spy locations to AOP text using code mapping, each with a type.
 *
 * Handles edge cases:
 * - Empty highlights array (returns empty array)
 * - Missing code mapping or AOP text (returns empty array)
 * - Errors during mapping (skips failed highlights, logs errors)
 *
 * @param highlights - Array of highlight inputs with type and location
 * @param codeMapping - The code mapping from spy to AOP
 * @param aopText - The AOP text content
 * @returns Object containing the typed highlights and error state
 *
 * @example
 * const { highlights, hasError } = useMultipleHighlightRanges({
 *   highlights: [
 *     { type: 'exception', location: exception?.location },
 *     { type: 'executed', location: executedLocation },
 *   ],
 *   codeMapping: automationData?.code_mapping,
 *   aopText: automationData?.english_code,
 * });
 */
export function useMultipleHighlightRanges({
  highlights,
  codeMapping,
  aopText,
}: {
  highlights: IHighlightInput[];
  codeMapping?: V1CodeMapping | null;
  aopText?: string | null;
}): IMultipleHighlightRangesResult {
  return useMemo(() => {
    // Early return if no highlights or missing required data
    if (!highlights || highlights.length === 0 || !codeMapping || !aopText) {
      return { highlights: [], hasError: false };
    }

    const typedHighlights: ITypedHighlight[] = [];
    let hasError = false;

    for (const highlight of highlights) {
      const { type, location } = highlight;

      // Skip if location is missing
      if (!location || !location.start_byte || !location.end_byte) {
        continue;
      }

      try {
        const ranges = mapSpyLocationToAopCharacterRanges({
          spyLocation: location,
          codeMapping: codeMapping,
          aopText: aopText,
        });

        // Only add if we got valid ranges
        if (ranges.length > 0) {
          typedHighlights.push({ type, ranges });
        }
      } catch (error) {
        hasError = true;
        logger.error(
          'useMultipleHighlightRanges: Error computing highlight ranges',
          error,
          {
            type,
            hasLocation: !!location,
            hasCodeMapping: !!codeMapping,
            hasAopText: !!aopText,
          },
        );

        // Capture error to Sentry for monitoring
        captureException(error as Error, {
          tags: {
            component: 'useMultipleHighlightRanges',
            action: 'mapSpyLocationToAopCharacterRanges',
            highlightType: type,
          },
          extra: {
            hasLocation: !!location,
            hasCodeMapping: !!codeMapping,
            hasAopText: !!aopText,
            locationStartByte: location.start_byte,
            locationEndByte: location.end_byte,
          },
        });
      }
    }

    return { highlights: typedHighlights, hasError };
  }, [highlights, codeMapping, aopText]);
}
